<script setup>
import { computed, shallowRef, watch } from 'vue';
import { useStore } from 'vuex';
import UiBadge from '@/components/ui/UiBadge.vue';
import UiPerson from '@/components/ui/UiPerson.vue';
import UiTableCard from '@/components/ui/UiTableCard.vue';
import AccountAccessDialog from '@/components/accounts/AccountAccessDialog.vue';
import AccountCreateDialog from '@/components/accounts/AccountCreateDialog.vue';
import AccountInfoDialog from '@/components/accounts/AccountInfoDialog.vue';
import AccountParentCreateDialog from '@/components/accounts/AccountParentCreateDialog.vue';
import AccountPasswordDialog from '@/components/accounts/AccountPasswordDialog.vue';
import PageTabs from '@/components/Utils/PageTabs.vue';
import { apiRequest } from '@/api/http';
import { usePendingAccounts } from '@/composables/usePendingAccounts';
import { useAsyncAction } from '@/composables/useAsyncAction';
import { useConfirm } from '@/composables/useConfirm';
import { useDialogs } from '@/composables/useDialogs';
import { useListResource } from '@/composables/useListResource';
import { useModuleOptions } from '@/composables/useModuleOptions';
import { useSessionGuard } from '@/composables/useSessionGuard';
import { ADMIN_ROLE_CODES, parseRoleIds, PREFECT_ROLE_CODES, RoleCode } from '@/constants/accounts';
import { formatDate } from '@/utils/format';

useSessionGuard();

// Accounts: manage verified users and verify new registrations. The page
// opens on Verified, so the For verification tab carries a count of the
// registrations waiting, which would otherwise go unnoticed. The server only
// sends the accounts in the viewer's department (JHS or SHS); the
// superadmin sees the whole school.
// Archived accounts can't sign in; only the superadmin archives, restores
// or sees them (the server enforces it too).
const store = useStore();
const isSuperadmin = computed(() => store.getters.activeRoleCode === RoleCode.Superadmin);
// Shared with the sidebar's Accounts pill.
const { count: pendingCount, refresh: loadPendingCount, set: setPendingCount, PENDING_URL } = usePendingAccounts();
const TABS = computed(() => [
  { id: 'verified', name: 'Verified' },
  { id: 'pending', name: 'For verification', count: pendingCount.value },
  ...(isSuperadmin.value ? [{ id: 'archived', name: 'Archived' }] : []),
]);
const tab = shallowRef('verified');

const LIST_URLS = {
  verified: '/user-details/getAllVerifiedUser',
  pending: PENDING_URL,
  archived: '/user-details/archivedUsers',
};
const { items, loading, error, load } = useListResource(() => apiRequest('get', LIST_URLS[tab.value]));
watch(tab, load, { immediate: true });

// While the pending list is open, its length is the count.
watch(items, (rows) => {
  if (tab.value === 'pending') setPendingCount(rows.length);
});
loadPendingCount();

const EMPTY_TEXT = {
  verified: (who) => `No verified ${who} accounts yet.`,
  pending: (who) => `No ${who} registrations are waiting for verification.`,
  archived: (who) => `No archived ${who} accounts.`,
};

// Verifying, deleting or adding an account changes both lists.
function reload() {
  load();
  if (tab.value !== 'pending') loadPendingCount();
}

const headers = [
  { title: 'User', key: 'name' },
  { title: 'Level', key: 'levelText', width: 150 },
  { title: 'Role', key: 'roleText', sortable: false },
];

// Each level gets its own badge colour so the column scans at a glance.
const LEVELS = {
  JHS: { label: 'Junior High', variant: 'blue' },
  SHS: { label: 'Senior High', variant: 'violet' },
  BOTH: { label: 'JHS & SHS', variant: 'green' },
};
const NO_LEVEL = { label: 'Not set', variant: 'dashed' };
const NO_CHILD = { label: 'No child linked', variant: 'dashed' };

// A parent's level is their linked children's (child_level from the server),
// which is also what decides whether the JHS or SHS admin sees them.
const isParent = (a) => findRoleCode(a.user_assignedModuleID) === RoleCode.Parent;
// Form 29 details are for employees: not parents or the superadmin.
const hasForm29 = (a) => ![RoleCode.Parent, RoleCode.Superadmin].includes(findRoleCode(a.user_assignedModuleID));
const levelOf = (a) => (isParent(a) ? LEVELS[a.child_level] ?? NO_CHILD : LEVELS[a.levelScope] ?? NO_LEVEL);

// The roles that matter when reading the list, in this order. Adviser is
// granted automatically from section assignment; the others by an admin.
// Every employee teaches, so one with no extra duty reads as Subject Teacher
// rather than leaving the column blank.
const { findRoleCode } = useModuleOptions();
const ROLE_BADGES = [
  { label: 'Admin', variant: 'violet', codes: ADMIN_ROLE_CODES },
  { label: 'Adviser', variant: 'blue', codes: [RoleCode.Adviser] },
  { label: 'Prefect of Discipline', variant: 'red', codes: PREFECT_ROLE_CODES },
];
const PARENT_ROLE = { label: 'Parent', variant: 'amber' };
const TEACHER_ROLE = { label: 'Subject Teacher', variant: 'gray' };

function rolesOf(account) {
  if (isParent(account)) return [PARENT_ROLE];
  const codes = parseRoleIds(account.user_subModules).map(findRoleCode);
  const roles = ROLE_BADGES.filter((role) => role.codes.some((code) => codes.includes(code)));
  return roles.length ? roles : [TEACHER_ROLE];
}

// Employee and parent accounts are managed apart, so the list shows one group
// at a time; the switch sits beside the title and applies to every tab.
const AUDIENCES = [
  { id: 'employees', name: 'Employees' },
  { id: 'parents', name: 'Parents' },
];
const audience = shallowRef('employees');

const rows = computed(() =>
  items.value.filter((a) => isParent(a) === (audience.value === 'parents')).map((a) => {
    const roleBadges = rolesOf(a);
    const level = levelOf(a);
    return { ...a, level, levelText: level.label, roleBadges, roleText: roleBadges.map((r) => r.label).join(', ') };
  }),
);

// One dialog at a time (see useDialogs).
const dialogs = useDialogs();
const accessOpen = dialogs.model('access');
const infoOpen = dialogs.model('info');
const passwordOpen = dialogs.model('password');
const createOpen = dialogs.model('create');
const createParentOpen = dialogs.model('createParent');

const { confirm } = useConfirm();
const { run } = useAsyncAction();

async function remove(account) {
  const ok = await confirm({
    title: 'Delete account?',
    description: `${account.name} will be permanently removed. This can't be undone.`,
    confirmText: 'Delete',
    destructive: true,
  });
  if (ok && (await run(() => apiRequest('delete', `/user-details/${account.id}`), { success: 'Account deleted.', error: 'Could not delete the account.' }))) {
    reload();
  }
}

async function archive(account) {
  const ok = await confirm({
    title: `Archive ${account.name}?`,
    description: "They won't be able to sign in, and they move to the Archived tab. Their records stay, and you can restore the account at any time.",
    confirmText: 'Archive',
    destructive: true,
  });
  if (ok && (await run(() => apiRequest('patch', `/user-details/${account.id}/archive`), { success: `${account.name} archived.`, error: 'Could not archive the account.' }))) {
    reload();
  }
}

async function restore(account) {
  if (await run(() => apiRequest('patch', `/user-details/${account.id}/restore`), { success: `${account.name} restored.`, error: 'Could not restore the account.' })) {
    reload();
  }
}

function rowActions(account) {
  if (tab.value === 'archived') {
    return [
      { title: 'Restore', icon: 'mdi-backup-restore', onClick: () => restore(account) },
      { title: 'Personal information', icon: 'mdi-account-outline', onClick: () => dialogs.open('info', account) },
    ];
  }
  if (tab.value === 'pending') {
    return [
      { title: 'Verify', icon: 'mdi-check-decagram-outline', onClick: () => dialogs.open('access', account) },
      { title: 'Delete', icon: 'mdi-trash-can-outline', destructive: true, onClick: () => remove(account) },
    ];
  }
  return [
    { title: 'Access', icon: 'mdi-shield-account-outline', onClick: () => dialogs.open('access', account) },
    { title: 'Personal information', icon: 'mdi-account-outline', onClick: () => dialogs.open('info', account) },
    { title: 'Set password', icon: 'mdi-key-outline', onClick: () => dialogs.open('password', account) },
    { title: 'Archive', icon: 'mdi-archive-arrow-down-outline', destructive: true, hidden: !isSuperadmin.value, onClick: () => archive(account) },
  ];
}
</script>

<template>
  <UiTableCard
    v-model:tab="tab"
    title="Accounts"
    description="Verify new registrations and manage who can access what."
    :tabs="TABS"
    :headers="headers"
    :items="rows"
    :loading="loading"
    :error="error"
    search-placeholder="Search accounts"
    :search-fields="['email']"
    :add-label="tab === 'archived' ? '' : audience === 'parents' ? 'Add parent account' : 'Add account'"
    :empty-text="EMPTY_TEXT[tab](audience === 'parents' ? 'parent' : 'employee')"
    row-label="name"
    :row-actions="rowActions"
    @add="dialogs.open(audience === 'parents' ? 'createParent' : 'create')"
  >
    <template #header-actions>
      <PageTabs v-model="audience" :items="AUDIENCES" aria-label="Account type" />
    </template>
    <template #[`item.name`]="{ item }">
      <UiPerson
        :name="item.name"
        :subtitle="item.archivedAt ? `${item.email} · archived ${formatDate(item.archivedAt)}` : item.email"
      />
    </template>
    <template #[`item.levelText`]="{ item }">
      <UiBadge :variant="item.level.variant">{{ item.level.label }}</UiBadge>
    </template>
    <template #[`item.roleText`]="{ item }">
      <span class="account-roles">
        <UiBadge v-for="role in item.roleBadges" :key="role.label" :variant="role.variant">{{ role.label }}</UiBadge>
      </span>
    </template>
  </UiTableCard>

  <AccountAccessDialog
    v-model="accessOpen"
    :account="dialogs.selected.value"
    :mode="tab === 'pending' ? 'verify' : 'update'"
    @saved="reload"
  />
  <AccountInfoDialog
    v-model="infoOpen"
    :account="dialogs.selected.value"
    :faculty="!!dialogs.selected.value && hasForm29(dialogs.selected.value)"
    @saved="reload"
  />
  <AccountPasswordDialog v-model="passwordOpen" :account="dialogs.selected.value" />
  <AccountCreateDialog v-model="createOpen" @saved="reload" />
  <AccountParentCreateDialog v-model="createParentOpen" @saved="reload" />
</template>

<style scoped>
.account-roles {
  display: inline-flex;
  flex-wrap: wrap;
  gap: 4px;
  padding: 4px 0;
}
</style>
